import { Alert, Button, Card, Checkbox, Col, DateField, Divider, EditButton, Icons, Row, Space, Tag, Typography, useDrawerForm } from '@pankod/refine-antd'
import { IResourceComponentsProps, useShow } from '@pankod/refine-core'
import { message } from 'antd'
import { UserAddresses } from 'components/orders/user_addresses'
import { SendEmailButton } from 'components/supplement/send_email_button'
import { HomePickupData, definitions } from 'interfaces'
import { IssuesFilesList } from 'pages/issues_files'
import { Dispatch, SetStateAction, createContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ClientStatusIdTypes, IssueLocationId, IssueStatusIdTypes, IssueSubTypeIdTypes, IssueTypeIdTypes } from 'types/types'
import { ADMITTED_STATUS, API_URL, DEFAULT_DATE_FORMAT, DEFAULT_DATE_TIME_FORMAT, DELIVERED_STATUS, ORDER_STATUS_ID_PENDING_PAYMENT, ORDER_STATUS_ID_REQUESTED, PROCESSED_STATUS, RETURN_ISSUE_METHOD_CORREOS, RETURN_ISSUE_METHOD_DELIVERY, SHIPPED_STATUS, capitalizeFirstLetter, currency, sendEmailRequest, supabaseClient } from 'utility'
import { currentSession } from 'utility/authLocal'
import { CLIENT_STATUSES_MAPPER, ISSUE_LOCATION_MAPPER, ISSUE_STATUS_MAPPER, ISSUE_SUB_TYPES_MAPPER, ISSUE_TYPES_MAPPER } from 'utility/mapper'
import { IssueDrawer } from './drawer'
import { IssueHistory } from './history'

type IssuesContextProps = { refetch?: boolean, setRefetch?: Dispatch<SetStateAction<boolean>> }
export const IssuesContext = createContext<IssuesContextProps>({})

export const IssueShow: React.FC<IResourceComponentsProps> = () => {
  const { t } = useTranslation()
  const [refetch, setRefetch] = useState(false)

  const [requestedDate, setRequestedDate] = useState<string>()
  const [processedDate, setProcessedDate] = useState<string>()
  const [supplementDate, setSupplementDate] = useState<string>()
  const [shippedDate, setShippedDate] = useState<string>()
  const [deliveredDate, setDeliveredDate] = useState<string>()
  const [homePickupSupplement, setHomePickupSupplement] = useState<definitions['supplements'] | null>(null)
  const [loadingCorreosTicket, setLoadingCorreosTicket] = useState(false)
  const [loadingSendCorreosLabel, setLoadingSendCorreosLabel] = useState(false)

  const {
    drawerProps,
    formProps,
    show,
    close
  } = useDrawerForm<definitions['issues']>({
    action: 'edit',
    resource: 'issues',
    redirect: false
  })

  const issuesFields = `id, order_id, created_at, internal, issue_type_id, issue_sub_type_id, client_status_id, issue_status_id, return_ticket_name, shipment_code, issue_return_error, reason, notes, notes_marketplace,
    request_date, shipping_method, shipper, tracking_id, issue_deadline, delivery_deadline, created_by, updated_at, deleted, is_opened, is_completed, is_wallapop, asigned_to, return_method, home_pickup_data, issue_items:items`

  const addressFields = 'id, email, name, surnames, phone_number, nif, additional_info, is_company, full_address'

  const { queryResult } = useShow<definitions['issues'] & { 'order_id': definitions['Order'], 'items': definitions['items'][], 'audit_order': definitions['audit_orders'][] }>({
    resource: 'issues',
    metaData: {
      select: `${issuesFields}, order_id!inner(*, marketplace_id(id, name), items(id, name), user(id, name, email, created_at,
        orders!user(id, total, created_at, status_id, syniva_price, shipping_price, shipping_insurance_price, custom_order_price, wallet_amount_used,
        supplements(supplement), items(shipping_price_to_madrid), marketplace_id(id, shipment_price, free_shipment_price))),
        shipping_address(${addressFields}, island(boat_extra_cost, plane_extra_cost)), billing_address(${addressFields}), status(name)), items(id, name), location`
    }
  })

  useEffect(() => {
    if (refetch) queryResult.refetch()
    setRefetch(false)
  }, [refetch])

  useEffect(() => {
    const loadData = async () => {
      const { data } = await supabaseClient.from<definitions['audit_orders']>('audit_orders').select('*').order('created_at', { ascending: false }).eq('resource_id', order.id)
      const { data: supData } = await supabaseClient.from<definitions['supplements']>('supplements').select('id, created_at')
        .eq('order_id', order.id)
        .eq('volumetric', true)
        .eq('deleted', false)
        .single()

      if (supData?.created_at) {
        setSupplementDate(supData.created_at)
      }

      const { data: homePickupSupData } = await supabaseClient.from<definitions['supplements']>('supplements')
        .select('id, supplement, status_id, description, email_sent_at, order_id')
        .eq('order_id', order.id)
        .eq('description', 'home_pickup')
        .eq('deleted', false)
        .order('created_at', { ascending: false })
        .maybeSingle()

      if (homePickupSupData) {
        setHomePickupSupplement(homePickupSupData)
      }

      if (queryResult.data) {
        queryResult.data.data.audit_order = data || []

        const sortedData = data?.sort((a, b) => a.id - b.id)

        setRequestedDate(sortedData?.find(ao => (ao.record as definitions['orders']).status_id === ORDER_STATUS_ID_REQUESTED)?.created_at)
        setProcessedDate(sortedData?.find(ao => (ao.record as definitions['orders']).status_id === PROCESSED_STATUS)?.created_at)
        // setSupplementDate(sortedData?.find(ao => (ao.record as definitions['orders']).status_id === SUPPLEMENT_STATUS)?.created_at)
        setShippedDate(sortedData?.find(ao => (ao.record as definitions['orders']).status_id === SHIPPED_STATUS)?.created_at)
        setDeliveredDate(sortedData?.find(ao => (ao.record as definitions['orders']).status_id === DELIVERED_STATUS)?.created_at)
      }
    }

    if (queryResult.data?.data.order_id.id) {
      loadData()
    }
  }, [queryResult.data])

  const { data } = queryResult
  const issue = data?.data

  const homePickupData = issue?.home_pickup_data as HomePickupData

  if (!issue) return null

  const { order_id: order } = issue
  if (!order) return null

  const leftDateItems = [
    <Typography key="created_at" style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative', paddingLeft: 18 }}>
      <span style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#c3fb00', display: 'inline-block', flexShrink: 0, boxShadow: '0 0 0 2px rgba(195,251,0,0.15)' }} />
      <span><b>{`${t('issues.fields.created_at')}: `}</b>{issue.order_id.created_at ? <DateField value={issue.order_id.created_at} format={DEFAULT_DATE_TIME_FORMAT} /> : '-'}</span>
    </Typography>,
    <Typography key="requested_date" style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative', paddingLeft: 18 }}>
      <span style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#c3fb00', display: 'inline-block', flexShrink: 0, boxShadow: '0 0 0 2px rgba(195,251,0,0.15)' }} />
      <span><b>{`${t('issues.fields.requested_date')}: `}</b>{requestedDate ? <DateField value={requestedDate} format={DEFAULT_DATE_TIME_FORMAT} /> : '-'}</span>
    </Typography>,
    <Typography key="processed_date" style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative', paddingLeft: 18 }}>
      <span style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#c3fb00', display: 'inline-block', flexShrink: 0, boxShadow: '0 0 0 2px rgba(195,251,0,0.15)' }} />
      <span><b>{`${t('issues.fields.processed_date')}: `}</b>{processedDate ? <DateField value={processedDate} format={DEFAULT_DATE_TIME_FORMAT} /> : '-'}</span>
    </Typography>,
    <Typography key="supplement_date" style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative', paddingLeft: 18 }}>
      <span style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#c3fb00', display: 'inline-block', flexShrink: 0, boxShadow: '0 0 0 2px rgba(195,251,0,0.15)' }} />
      <span><b>{`${t('issues.fields.supplement_date')}: `}</b>{supplementDate ? <DateField value={supplementDate} format={DEFAULT_DATE_TIME_FORMAT} /> : '-'}</span>
    </Typography>,
    <Typography key="shipped_date" style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative', paddingLeft: 18 }}>
      <span style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#c3fb00', display: 'inline-block', flexShrink: 0, boxShadow: '0 0 0 2px rgba(195,251,0,0.15)' }} />
      <span><b>{`${t('issues.fields.shipped_date')}: `}</b>{shippedDate ? <DateField value={shippedDate} format={DEFAULT_DATE_TIME_FORMAT} /> : '-'}</span>
    </Typography>
  ]

  const rightDateItems = [
    <Typography key="delivered_date" style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative', paddingLeft: 18 }}>
      <span style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#c3fb00', display: 'inline-block', flexShrink: 0, boxShadow: '0 0 0 2px rgba(195,251,0,0.15)' }} />
      <span><b>{`${t('issues.fields.delivered_date')}: `}</b>{deliveredDate ? <DateField value={deliveredDate} format={DEFAULT_DATE_TIME_FORMAT} /> : '-'}</span>
    </Typography>,
    <Typography key="created_at_issue" style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative', paddingLeft: 18 }}>
      <span style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#c3fb00', display: 'inline-block', flexShrink: 0, boxShadow: '0 0 0 2px rgba(195,251,0,0.15)' }} />
      <span><b>{`${t('issues.fields.created_at_issue')}: `}</b>{issue.created_at ? <DateField value={issue.created_at} format={DEFAULT_DATE_TIME_FORMAT} /> : '-'}</span>
    </Typography>,
    <Typography key="issue_deadline" style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative', paddingLeft: 18 }}>
      <span style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#c3fb00', display: 'inline-block', flexShrink: 0, boxShadow: '0 0 0 2px rgba(195,251,0,0.15)' }} />
      <span><b>{`${t('issues.fields.issue_deadline')}: `}</b>{issue.issue_deadline ? <DateField value={issue.issue_deadline} format={DEFAULT_DATE_FORMAT} /> : '-'}</span>
    </Typography>,
    <Typography key="request_date" style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative', paddingLeft: 18 }}>
      <span style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#c3fb00', display: 'inline-block', flexShrink: 0, boxShadow: '0 0 0 2px rgba(195,251,0,0.15)' }} />
      <span><b>{`${t('issues.fields.request_date')}: `}</b>{issue.request_date ? <DateField value={issue.request_date} format={DEFAULT_DATE_TIME_FORMAT} /> : '-'}</span>
    </Typography>
  ]

  const handleDownloadLabel = async () => {
    if (!issue.id || !issue.order_id?.id) return null

    try {
      const response = await fetch(`${API_URL}/actions/return-ticket`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentSession()?.access_token}`
        },
        body: JSON.stringify({
          orderId: issue.order_id.id,
          issueId: issue.id
        })
      })
      if (!response.ok) {
        console.error('Error fetching return ticket URL')
        return null
      }

      const { signedURL } = await response.json()
      if (signedURL) {
        return signedURL
      }
      return null
    } catch (error) {
      console.error('Error creating signed URL:', error)
      return null
    }
  }

  const handleViewReturnLabel = async () => {
    const signedURL = await handleDownloadLabel()
    if (signedURL) {
      window.open(signedURL, '_blank', 'noreferrer')
    }
  }

  const handleSendCorreosLabel = async () => {
    if (!issue.id) return
    setLoadingSendCorreosLabel(true)

    try {
      const { data: issueData, error } = await supabaseClient.from<definitions['issues']>('issues').select('*').eq('id', issue.id).single()
      if (error || !issueData) {
        throw new Error(t('issues.messages.send_ticket_error'))
      }

      await sendEmailRequest(issueData, 'issues',
        () => message.success(t('issues.messages.send_ticket_success')),
        () => message.error(t('issues.messages.send_ticket_error')),
        true
      )
    } catch (error) {
      console.error('Error sending Correos ticket email:', error)
      message.error((error as any)?.message || t('issues.messages.send_ticket_error'))
    } finally {
      setLoadingSendCorreosLabel(false)
    }
  }

  const handleGenerateCorreosTicket = async () => {
    if (!issue.id || !issue.order_id?.id) return
    const orderId = issue.order_id.id
    const issueId = issue.id

    try {
      setLoadingCorreosTicket(true)

      const response = await fetch(`${API_URL}/issues/return/${issueId}/${orderId}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          authorization: `Bearer ${currentSession()?.access_token}`
        }
      })

      if (!response.ok) {
        let errorMsg = t('issues.messages.generate_ticket_error')
        try {
          const errorData = await response.json()
          if (errorData?.error || errorData?.msg) {
            errorMsg = errorData.error || errorData.msg
          }
        } catch (_) { }
        throw new Error(errorMsg)
      }

      const signedURL = await handleDownloadLabel()
      if (!signedURL) {
        throw new Error(t('issues.messages.signed_url_error'))
      }

      window.open(signedURL, '_blank', 'noreferrer')
      setRefetch(true)
      message.success(t('issues.messages.generate_ticket_success'))
    } catch (error) {
      console.error('Correos ticket generation error:', error)
      const errMsg = (error as any)?.message || t('issues.messages.generate_ticket_error')
      message.error(errMsg)
    } finally {
      setLoadingCorreosTicket(false)
    }
  }

  const handleViewShipment = async () => {
    if (!issue.shipment_code) return
    const url = `https://www.correos.es/es/es/herramientas/localizador/envios/detalle?tracking-number=${issue.shipment_code}`
    window.open(url, '_blank')
  }

  const handleRefresh = () => {
    setRefetch(true)
  }

  return (
    <IssuesContext.Provider value={{ refetch, setRefetch }}>
      <Row gutter={[15, 15]}>
        <Col xs={24}>
          <Card bordered={false} style={{ marginBottom: 15 }}>
            <Space
              direction="horizontal"
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between'
              }}
            >
              <Typography.Text style={{ fontWeight: 600, fontSize: '22px' }}>
                {/* put here the info icon, and the 'Incidencia #' and the issue.id */}
                <Icons.InfoCircleOutlined style={{ color: '#c3fb00' }} />
                <span style={{ margin: '8px 8px 0' }}>{t('issues.show.issue_id', { id: issue.id })}</span>
                <a href={`/orders/show/${order.id}`} target='_blank' rel='noreferrer'>
                  <Icons.LinkOutlined />
                  {' '}
                  {t('issues.show.order_id', { order_id: order.id })}
                </a>
                <Tag style={{ marginLeft: '2rem', padding: 5, fontSize: 16 }} color={issue.is_opened ? 'red' : 'green'}>
                  {t(`issues.issue_status.${issue.is_opened ? 'open' : 'close'}`)}
                </Tag>
                {(issue.asigned_to) ? `${t('issues.fields.asigned_to')} ${issue.asigned_to}` : null}
              </Typography.Text>
              <EditButton onClick={() => show()} />
            </Space>
          </Card>
        </Col>
      </Row>
      <Row gutter={[15, 15]}>
        <Col xs={24} xl={12}>
          <UserAddresses order={order} canEdit={false} comesFromIssue={true} />
        </Col>
        <Col xs={24} xl={12}>
          <Row gutter={[15, 15]} style={{ height: '100%' }}>
            <Col xs={24}>
              <Card bordered={false} style={{ height: '100%' }} bodyStyle={{ padding: 0 }}>
                <Typography style={{ fontWeight: 600, fontSize: '18px', margin: '1rem 1rem 0' }}>
                  <Icons.InfoCircleOutlined style={{ color: '#c3fb00' }} />
                  <span style={{ marginLeft: 8 }}>{t('issues.titles.issue_details')}</span>
                </Typography>
                <div style={{ display: 'flex', width: '100%', height: '100%' }}>
                  <div style={{ flex: '0 0 40%', maxWidth: '40%', boxSizing: 'border-box', padding: '1rem 0.5rem 1rem 1rem' }}>
                    <Typography>
                      <b>{`${t('issues.fields.syniva_status')}: `}</b>
                      {capitalizeFirstLetter(order.status.name)}
                    </Typography>
                    <Typography>
                      <b>{`${t('issues.fields.issue_type_id')}: `}</b>
                      {t(`issues.issue_types.${issue.issue_type_id ? ISSUE_TYPES_MAPPER[issue.issue_type_id as IssueTypeIdTypes] : '-'}`)}
                    </Typography>
                    <Typography>
                      <b>{`${t('issues.fields.issue_sub_type_id')}: `}</b>
                      {issue.issue_sub_type_id ? t(`issues.issue_sub_types.${ISSUE_SUB_TYPES_MAPPER[issue.issue_sub_type_id as IssueSubTypeIdTypes]}`) : '-'}
                    </Typography>
                    <Typography>
                      <b>{`${t('issues.fields.client_status_id')}: `}</b>
                      {issue.client_status_id ? t(`issues.client_statuses.${CLIENT_STATUSES_MAPPER[issue.client_status_id as ClientStatusIdTypes]}`) : '-'}
                    </Typography>
                    <Typography>
                      <b>{`${t('issues.fields.issue_status_id')}: `}</b>
                      {issue.issue_status_id ? t(`issues.issue_statuses.${ISSUE_STATUS_MAPPER[issue.issue_status_id as IssueStatusIdTypes]}`) : '-'}
                    </Typography>
                    <Typography>
                      <b>{`${t('issues.fields.location')}: `}</b>
                      {issue?.location ? t(`issues.issue_location.${ISSUE_LOCATION_MAPPER[issue.location as IssueLocationId]}`) : '-'}
                    </Typography>
                    <Typography>
                      <b>{`${t('issues.fields.is_completed')}: `}</b>
                      <Checkbox checked={issue.is_completed} disabled={true} />
                    </Typography>
                    <Typography>
                      <b>{`${t('issues.fields.is_wallapop')}: `}</b>
                      <Checkbox checked={issue.is_wallapop} disabled={true} />
                    </Typography>
                    <Typography>
                      <b>{`${t('issues.fields.internalBy')}: `}</b>
                      {!issue.internal ? t('issues.fields.client') : t('issues.fields.internal')}
                    </Typography>
                  </div>
                  <div style={{ flex: '0 0 60%', maxWidth: '60%', boxSizing: 'border-box', padding: '1rem 0.5rem 1rem 1rem' }}>
                    <Typography style={{ whiteSpace: 'pre-line' }}>
                      <b>{`${t('issues.fields.marketplace')}: `}</b>
                      {issue.order_id.marketplace_id.name}
                    </Typography>
                    <Typography style={{ whiteSpace: 'pre-line' }}>
                      <b>{`${t('issues.fields.notes_marketplace')}: `}</b>
                      {issue.notes_marketplace ?? '-'}
                    </Typography>
                    <Typography>
                      <b>{`${t('issues.fields.involved_products')}: `}</b>
                      <ul style={{ marginBottom: 0 }}>
                        {((queryResult.data?.data as any)?.issue_items && (queryResult.data?.data as any)?.issue_items.length > 0)
                          ? (queryResult.data?.data?.order_id?.items || []).filter((item) => (queryResult.data?.data as any)?.issue_items.includes(item.id.toString())).map((item) => <li key={`${item.id}-${item.name}`}>{item.name}</li>)
                          : (queryResult.data?.data?.items as { id: string; name: string }[]).map((item) => <li key={`${item.id}-${item.name}`}>{item.name}</li>)
                        }
                      </ul>
                    </Typography>
                    <Typography>
                      <b>{`${t('issues.fields.reason')}: `}</b>
                      {issue.reason ?? '-'}
                    </Typography>
                    {/* <Typography>
                      <b>{`${t('issues.fields.delivery_deadline')}: `}</b>
                      {issue.delivery_deadline && <DateField value={issue.delivery_deadline} format={DEFAULT_DATE_FORMAT} />}
                    </Typography> */}
                  </div>
                </div>
              </Card>
            </Col>
            {issue.return_method && <Col xs={24}>
              <Card bordered={false} style={{ height: '100%' }} bodyStyle={{ padding: 0 }}>
                <div style={{ display: 'flex', width: '100%', height: '100%' }}>
                  <div style={{ width: '100%', boxSizing: 'border-box', padding: '1rem 0.5rem 1rem 1rem' }}>
                    <Typography style={{ fontWeight: 600, fontSize: '18px', marginBottom: 8 }}>
                      <Icons.RollbackOutlined style={{ color: '#c3fb00' }} />
                      <span style={{ marginLeft: 8 }}>{t('issues.titles.return_client')}</span>
                    </Typography>
                    <Typography>
                      <b>{`${t('issues.fields.return_method')}: `}</b>
                      {issue.return_method ? t(`issues.return_methods.${issue.return_method}`) : '-'}
                    </Typography>
                    {issue.return_method === RETURN_ISSUE_METHOD_CORREOS && (
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(170px, 1fr))', gap: 8, marginTop: 8 }}>
                          <Button
                            style={{ width: '100%', whiteSpace: 'normal', padding: '0 3rem' }}
                            icon={<Icons.EyeOutlined />}
                            disabled={!issue.return_ticket_name}
                            onClick={handleViewReturnLabel}
                          >
                            {t('issues.fields.view_ticket_button')}
                          </Button>
                          <Button
                            style={{ width: '100%', whiteSpace: 'normal', padding: '0 3rem' }}
                            type="primary"
                            icon={<Icons.SendOutlined />}
                            loading={loadingSendCorreosLabel}
                            disabled={!issue.return_ticket_name}
                            onClick={handleSendCorreosLabel}
                          >
                            {t('issues.fields.send_ticket_button')}
                          </Button>
                          <Button
                            style={{ width: '100%', whiteSpace: 'normal', padding: '0 3rem' }}
                            type={!issue.return_ticket_name ? 'primary' : 'default'}
                            icon={<Icons.TagOutlined />}
                            loading={loadingCorreosTicket}
                            onClick={handleGenerateCorreosTicket}
                          >
                            {t('issues.fields.generate_ticket_button')}
                          </Button>
                          <Button
                            style={{ width: '100%', whiteSpace: 'normal', padding: '0 3rem' }}
                            icon={<Icons.EnvironmentOutlined />}
                            disabled={!issue.return_ticket_name || !issue.shipment_code}
                            onClick={handleViewShipment}
                          >
                            {t('issues.fields.incidence_btn_view_shipment')}
                          </Button>
                        </div>
                        {!issue.return_ticket_name && (
                          <Alert
                            message={t('issues.fields.label_generation_error')}
                            type="error"
                            showIcon
                            icon={<Icons.ExclamationCircleOutlined />}
                            style={{ marginTop: 8, marginBottom: 8 }}
                          />
                        )}
                      </Space>
                    )}
                    {issue.return_method === RETURN_ISSUE_METHOD_DELIVERY && homePickupData && (
                      <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 16, alignItems: 'stretch' }}>
                          <div>
                            <Typography>
                              <b>{t('issues.fields.home_pickup.home_pickup_address')}: </b>
                              {homePickupData.address ?? '-'}
                            </Typography>
                            <Typography>
                              <b>{t('issues.fields.home_pickup.home_pickup_date')}: </b>
                              {homePickupData.date ?? '-'}
                            </Typography>
                            <Typography>
                              <b>{t('issues.fields.home_pickup.home_pickup_observations')}: </b>
                              {homePickupData.observations ?? '-'}
                            </Typography>
                          </div>
                          <Divider type="vertical" style={{ height: '100%' }} />
                          <div>
                            <Typography>
                              <b>{t('issues.fields.home_pickup.home_pickup_phone')}: </b>
                              {homePickupData.phone ?? '-'}
                            </Typography>
                            <Typography>
                              <b>{t('issues.fields.home_pickup.home_pickup_schedule')}: </b>
                              {homePickupData.schedule ? t(`issues.fields.home_pickup.home_pickup_schedule_${homePickupData.schedule}`) : '-'}
                            </Typography>
                          </div>
                        </div>
                        {homePickupSupplement && (
                          <Card size="small" style={{ width: '100%', marginTop: 16, borderRadius: 8 }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                              <Typography>
                                <b>{t('issues.fields.home_pickup.home_pickup_supplement_title')}</b>
                              </Typography>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                                <Typography style={{ flex: '1 1 auto', minWidth: 0 }}>
                                  {t('issues.fields.home_pickup_title')}
                                </Typography>
                                <Typography style={{ whiteSpace: 'nowrap' }}>{currency(homePickupSupplement.supplement)}</Typography>
                                <Tag color={homePickupSupplement.status_id === ADMITTED_STATUS ? 'green' : homePickupSupplement.status_id === ORDER_STATUS_ID_PENDING_PAYMENT ? 'blue' : undefined}>
                                  {homePickupSupplement.status_id === ADMITTED_STATUS
                                    ? t('issues.fields.home_pickup.home_pickup_supplement_status_admitted')
                                    : t('issues.fields.home_pickup.home_pickup_supplement_status_pending_payment')}
                                </Tag>
                                <SendEmailButton supplement={homePickupSupplement} refreshTable={handleRefresh} isSupplement={true} isEstimatedCost={false} />
                              </div>
                            </Space>
                          </Card>
                        )}
                      </Space>
                    )}
                    </div>
                </div>
              </Card>
            </Col>}
            <Col xs={24}>
              <Card bordered={false} style={{ height: '100%' }}>
                <Typography style={{ fontWeight: 600, fontSize: '18px', marginBottom: 8 }}>
                  <Icons.CarOutlined style={{ color: '#c3fb00' }} />
                  <span style={{ marginLeft: 8 }}>{t('issues.titles.shipment_tracking')}</span>
                </Typography>
                <Space
                  direction="vertical"
                  style={{
                    width: '100%'
                  }}
                >
                  <Typography>
                    <b>{`${t('issues.fields.shipping_method')}: `}</b>
                    {issue.shipping_method ?? '-'}
                  </Typography>
                  <Typography>
                    <b>{`${t('issues.fields.shipper')}: `}</b>
                    {issue.shipper || t('issues.fields.shipper_not_available')}
                  </Typography>
                  <Typography>
                    <b>{`${t('issues.fields.tracking_id')}: `}</b>
                    {issue.tracking_id ?? '-'}
                  </Typography>
                </Space>
              </Card>
            </Col>
          </Row>
        </Col>
        <Col xs={24} xl={12}>
          <Card bordered={false} style={{ height: '100%' }}>
            <Typography style={{ fontWeight: 600, fontSize: '18px', marginBottom: 8 }}>
              <Icons.ClockCircleOutlined style={{ color: '#c3fb00' }} />
              <span style={{ marginLeft: 8 }}>{t('issues.titles.dates')}</span>
            </Typography>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '0.75rem 1.5rem', alignItems: 'start' }}>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: 4, top: 6, bottom: 6, width: 2, borderRadius: 999, backgroundColor: 'rgba(195, 251, 0, 0.38)' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {leftDateItems}
                </div>
              </div>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: 4, top: 6, bottom: 6, width: 2, borderRadius: 999, backgroundColor: 'rgba(195, 251, 0, 0.38)' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {rightDateItems}
                </div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card bordered={false} style={{ height: '100%' }}>
            <Typography style={{ fontWeight: 600, fontSize: '18px' }}>
              <Icons.FileTextOutlined style={{ color: '#c3fb00' }} />
              <span style={{ marginLeft: 8 }}>{t('issues.titles.notes')}</span>
            </Typography>
            <Space direction="vertical" style={{ width: '100%', height: '100%' }}>
                {issue.notes && (
                  <Typography style={{ whiteSpace: 'pre-line', marginTop: 8 }}>
                    {issue.notes}
                  </Typography>
                )}
              <Space>
                <IssueHistory issue={issue} onlyNotes={true} />
              </Space>
            </Space>
          </Card>
        </Col>
        <Col xs={12}>
          <Card bordered={false} style={{ maxHeight: '600px', height: '100%' }}>
            <Space direction="vertical" style={{ width: '100%', height: '100%' }}>
              <Space
                direction="vertical"
                style={{
                  width: '100%'
                }}
              >
                <IssueHistory issue={issue} field="client_status_id" />
              </Space>
            </Space>
          </Card>
        </Col>
        <Col xs={12}>
          <Card bordered={false} style={{ maxHeight: '600px', height: '100%' }}>
            <Space direction="vertical" style={{ width: '100%', height: '100%' }}>
              <Space
                direction="vertical"
                style={{
                  width: '100%'
                }}
              >
                <IssueHistory issue={issue} field="issue_status_id" />
              </Space>
            </Space>
          </Card>
        </Col>
        <Col xs={24}>
          <Card bordered={false} style={{ maxHeight: '600px', height: '100%' }}>
            <Space direction="vertical" style={{ width: '100%', height: '100%' }}>
              <Space
                direction="vertical"
                style={{
                  width: '100%'
                }}
              >
                <IssueHistory issue={issue} />
              </Space>
            </Space>
          </Card>
        </Col>
        <Col xs={24}>
          <Card bordered={false} style={{ height: '100%' }}>
            <Space direction="vertical" style={{ width: '100%', height: '100%' }}>
              <Space
                direction="vertical"
                style={{
                  width: '100%'
                }}
              >
                <IssuesFilesList issue={issue} userOrderEmail={issue.order_id.user.email ?? null} />
              </Space>
            </Space>
          </Card>
        </Col>
      </Row>

      <IssueDrawer
        drawerProps={drawerProps}
        formProps={formProps}
        close={close}
        isCreate={false}
        orderId={order.id}
      />
    </IssuesContext.Provider>
  )
}
